<template>
  <div class="min-h-full flex flex-col items-center justify-start" v-if="bootstrapped">
    <div class="fixed inset-0 pointer-events-none">
      <div id="circle-top"></div>
      <div id="circle-bottom"></div>
    </div>

    <div class="z-0 flex w-full">
      <div
        class="sidebar absolute min-h-full z-1 max-h-screen overflow-auto left-0 top-0 bottom-0 hidden lg:flex lg:flex-col transition-width ease-in duration-150 p-4"
        :class="{ 'lg:w-sidebar--opened': leftMenuOpened, 'lg:w-sidebar': !leftMenuOpened }"
      >
        <Navigation v-model:open="leftMenuOpened" :navItems="navItems" :logo="navigationLogo" />
      </div>
      <div
        v-if="showTopBar"
        class="top-bar fixed z-10 top-0 right-0 transition-all ease-in duration-150 left-0"
        :class="{ 'lg:left-top-bar--opened': leftMenuOpened, 'lg:left-top-bar': !leftMenuOpened }"
        style="background-color: #f5f3fe"
      >
        <div class="w-full pt-3 pb-3 absolute flex items-center z-0 px-6 min-h-16">
          <slot name="header" />
          <div class="flex ml-auto items-center">
            <Quota v-if="showQuota && false" class="mr-2 sm:mr-4" @click="onQuotaClick" />
            <Icon name="publish" v-tooltip="'Quick Publish'" class="w-6 h-6 cursor-pointer mr-4" v-if="projectId" @click="onShowPublishModalClick" />
            <Icon name="faq" v-tooltip="'Help'" class="w-5 h-5 cursor-pointer mr-4" @click="SET_SHOW_SIDE_PANEL(true)" />
            <Icon name="notification" v-tooltip="'Notifications'" class="w-6 h-6 cursor-pointer" @click="showNotificationPopup = true" v-outside-click="closeNotificationPopup" />

            <Notifications v-if="showNotificationPopup" />
          </div>
        </div>
      </div>
      <div
        class="main-content w-full h-screen flex flex-col"
        :class="{ 'lg:pl-main-content--opened': leftMenuOpened, 'lg:pl-main-content': !leftMenuOpened, 'pt-14 lg:pt-14': showTopBar, 'lg:pt-4': !showTopBar }"
      >
        <div class="flex-grow flex flex-col overflow-auto">
          <slot></slot>
        </div>
        <div class="h-14 inline lg:hidden bottom-navigation">
          <BottomNavigation :navItems="navItems" :logo="navigationLogo" />
        </div>
      </div>
    </div>

    <SidePanel />
    <Modal
      v-if="showPublishModal"
      title="Publish New Version"
      :primary-button="$t('publishing.publish')"
      :secondary-button="$t('cancel')"
      :primary-button-loading="publishing"
      :secondary-button-disabled="publishing"
      close-button
      sheetbelowsm
      @primary="onPublishClick"
      @close="showPublishModal = false"
      @secondary="showPublishModal = false"
    >
      <div class="w-full md:w-600 px-10 my-5" v-html="$t('publishing.new_publish_question', { nextVersion })"></div>
    </Modal>
  </div>
</template>

<script>
import { mapMutations, mapState, mapActions } from 'vuex';
import { FEATURES } from '@/constants';
import SidePanel from '@/components/help/SidePanel.vue';
import { apiGetProjectPublishList, apiPostProjectPublish } from '../../helpers/api';

export default {
  name: 'DefaultLayout',
  components: { SidePanel },
  props: {
    showTopBar: Boolean,
    showAccounts: Boolean,
  },
  data() {
    return {
      leftMenuOpened: false,
      showNotificationPopup: false,
      publishing: false,
      publishes: {},
      showPublishModal: false,
      navItems: [
        { label: 'navigation.dashboard', to: '/', icon: 'dashboard' },
        { label: 'navigation.projects', to: '/project', icon: 'token' },
        { label: 'navigation.settings', to: '/user/settings', icon: 'settings' },
        { label: 'navigation.admin', to: '/admin', icon: 'admin_panel_settings', restrictTo: FEATURES.ADMIN_DASHBOARD },
        { label: 'navigation.logout', to: '/logout', icon: 'logout' },
      ],
    };
  },
  computed: {
    ...mapState(['bootstrapped', 'features', 'theme']),
    navigationLogo() {
      if (this.theme.startsWith('white')) {
        return '/img/clients/fidsy/fidsy_bubble_icon.svg';
      }
      return '/img/clients/fidsy/fidsy_logo_white.svg';
    },
    showQuota() {
      return this.features.includes(FEATURES.SETTINGS_SUBSCRIPTION);
    },
    nextVersion() {
      const versions = this.publishList.map((r) => parseInt(r.version, 10));
      return Number.isInteger(Math.max(...versions)) ? Math.max(...versions) + 1 : 1;
    },
    publishList() {
      return Object.values(this.publishes).sort((a, b) => b.version - a.version);
    },
    projectId() {
      return this.$route.params.projectId;
    },
  },
  methods: {
    ...mapActions(['showToastMessage']),
    ...mapMutations(['SET_SHOW_SIDE_PANEL']),
    async onShowPublishModalClick() {
      await this.fetchPublishData();
      this.showPublishModal = true;
    },
    onQuotaClick() {
      this.$router.push('/user/subscriptions');
    },
    closeNotificationPopup() {
      this.showNotificationPopup = false;
    },
    async onPublishClick() {
      this.publishing = true;
      try {
        await this.fetchPublishData();
        const response = await apiPostProjectPublish({ project_id: this.projectId, version: this.nextVersion });
        if (response.status === 200) {
          this.showToastMessage({ message: this.$t('publishing.version_published', { version: this.nextVersion }), type: 'success' });
          this.showPublishModal = false;
        } else {
          this.showToastMessage({ title: response?.data?.message || this.$t('publishing.publish_failed'), type: 'error' });
        }
      } catch (e) {
        this.showToastMessage({ title: this.$t('publishing.publish_failed'), type: 'error' });
      }
      this.publishing = false;
    },
    async fetchPublishData() {
      try {
        const response = await apiGetProjectPublishList(this.projectId);
        if (response.status === 200) {
          this.publishes = response.data;
        } else {
          this.showToastMessage({ title: response?.data?.message || this.$t('publishing.failed_to_get_data'), type: 'error' });
        }
      } catch (e) {
        this.showToastMessage({ title: this.$t('publishing.failed_to_get_data'), type: 'error' });
      }
    },
  },
};
</script>
<style lang="scss" scoped>
#circle-top {
  position: absolute;
  width: 884px;
  height: 884px;
  left: 665px;
  top: -525px;
  background: #f1eefd;
  filter: blur(200px);
}
#circle-bottom {
  position: absolute;
  width: 698px;
  height: 698px;
  left: 674px;
  top: 665px;
  background: #ccd9ff;
  filter: blur(350px);
}
</style>
